import { ResponsiveTable, Spinner, useTranslation } from '@hbs-us/ui';

import { useJobFormatters } from '../useJobFormatters.js';
import { useJobTimeEntriesQuery } from '../useJobQuery.js';

/**
 * The Labor tab — the job's time-entries (date · employee · hours · note). Sourced from
 * `GET /api/v1/jobs/:id/time-entries` (server-computed, tenant-from-principal). The SPA renders
 * the returned rows verbatim and never re-aggregates (SEC-11 / CARVE-07). Every color is a locked
 * token (no hex); hours render in IBM Plex Mono for column alignment.
 *
 * `ResponsiveTable` reflows the four columns to label:value cards below `md`; at `md`+ it is the
 * same table it has always been.
 */

export function LaborTab({ jobId }: { jobId: number }) {
  const { t } = useTranslation();
  const fmt = useJobFormatters();
  const query = useJobTimeEntriesQuery(jobId);

  if (query.isPending) {
    return (
      <div className="flex items-center justify-center py-12">
        <Spinner label={t('common:status.loading')} />
      </div>
    );
  }

  if (query.isError) {
    return (
      <p role="alert" className="text-destructive text-sm">
        {t('jobs:labor.error')}
      </p>
    );
  }

  const entries = query.data?.entries ?? [];

  if (entries.length === 0) {
    return (
      <div className="rounded-md border border-border bg-card p-6">
        <p className="text-muted-foreground text-sm">{t('jobs:labor.empty')}</p>
      </div>
    );
  }

  return (
    <ResponsiveTable className="rounded-md border border-border bg-card">
      <table className="w-full border-collapse text-left text-sm">
        <caption className="sr-only">{t('jobs:labor.caption')}</caption>
        <thead>
          <tr className="border-border border-b text-muted-foreground text-xs uppercase tracking-wide">
            <th scope="col" className="px-4 py-3 font-medium">
              {t('jobs:labor.date')}
            </th>
            <th scope="col" className="px-4 py-3 font-medium">
              {t('jobs:labor.employee')}
            </th>
            <th scope="col" className="px-4 py-3 text-right font-medium">
              {t('jobs:labor.hours')}
            </th>
            <th scope="col" className="px-4 py-3 font-medium">
              {t('jobs:labor.note')}
            </th>
          </tr>
        </thead>
        <tbody>
          {entries.map((entry) => (
            <tr key={entry.id} className="border-border/60 border-b last:border-b-0">
              <td className="px-4 py-3 font-mono text-foreground">{entry.date}</td>
              <td className="px-4 py-3 text-foreground">
                {entry.employeeName || t('jobs:labor.unknownEmployee')}
              </td>
              <td className="px-4 py-3 text-right font-mono text-foreground">
                {fmt.hours(Number(entry.hours))}
              </td>
              <td className="px-4 py-3 text-muted-foreground">
                {entry.note || t('jobs:labor.noNote')}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </ResponsiveTable>
  );
}
